/**
 * Exporting a screen (0040): the same figures the screen shows, to Excel or
 * to PDF.
 *
 *  - Excel is built in the browser with SheetJS, loaded only when asked for.
 *    Every workbook starts with an "About" sheet saying what it is, for
 *    which period, when and by whom it was exported, so a file found in a
 *    mailbox months later still explains itself.
 *  - PDF is the browser's own "Save as PDF" of a print layout
 *    (src/styles/report.css): no PDF library, text stays searchable, and it
 *    works offline. Before printing, the page is narrowed to the paper's
 *    width for a moment so the charts redraw to fit, and the document title
 *    becomes the file name the browser suggests.
 */
import type { Column } from '@/components/ui/DataTable';
import { dateTime, isoDate } from '@/lib/format';

export type Cell = string | number | boolean | null | undefined;
export interface Sheet {
  name: string;
  /** Objects (one per row, keys are the headings) or rows of cells. */
  rows: Record<string, Cell>[] | Cell[][];
}
export interface ExportMeta {
  title: string;
  subtitle?: string;
  by?: string | null;
}

/** A file name: letters, digits and dashes, ending in a date — the one the
 *  base already ends in (a back-dated screen), otherwise today's. */
export function fileName(base: string, date = isoDate()): string {
  const clean = base.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  return /\d{4}-\d{2}-\d{2}$/.test(clean) ? `suremix-${clean}` : `suremix-${clean}-${date}`;
}

/** Excel sheet names: 31 characters, none of []:*?/\ , and unique. */
function sheetName(name: string, used: Set<string>): string {
  const base = (name.replace(/[[\]:*?/\\]/g, ' ').trim() || 'Sheet').slice(0, 31);
  let out = base;
  for (let i = 2; used.has(out.toLowerCase()); i++) out = `${base.slice(0, 28)} ${i}`;
  used.add(out.toLowerCase());
  return out;
}

/** A DataTable's rows as the table shows them: one column per heading,
 *  the sort/search value where a column has one (a number stays a number),
 *  otherwise the row's own field. Columns with no heading are skipped. */
export function tableRows<T>(columns: Column<T>[], rows: T[] | undefined): Record<string, Cell>[] {
  const cols = columns.filter((c) => c.label);
  return (rows ?? []).map((r) => {
    const out: Record<string, Cell> = {};
    for (const c of cols) {
      const v = c.value ? c.value(r) : (r as Record<string, unknown>)[c.key];
      out[c.label] = v == null ? '' : (typeof v === 'object' ? JSON.stringify(v) : (v as Cell));
    }
    return out;
  });
}

/** Label / value / unit rows for a panel of tiles. */
export function figures(rows: [string, Cell, string?][]): Cell[][] {
  return [['Figure', 'Value', 'Unit'], ...rows.map(([l, v, u]) => [l, v ?? '', u ?? ''])];
}

export async function exportWorkbook(file: string, sheets: Sheet[], meta: ExportMeta): Promise<void> {
  const XLSX = await import('xlsx');
  const wb = XLSX.utils.book_new();
  const used = new Set<string>();

  const about: Cell[][] = [
    [meta.title],
    ...(meta.subtitle ? [[meta.subtitle]] : []),
    [`Exported ${dateTime(new Date().toISOString())}${meta.by ? ` by ${meta.by}` : ''}`],
    ['Suremix Operations Platform — the figures are the screen\'s, computed in the database.'],
    [],
    ['Sheets'],
    ...sheets.map((s) => [s.name]),
  ];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(about), sheetName('About', used));

  for (const s of sheets) {
    const isArrays = Array.isArray(s.rows[0]);
    const ws = s.rows.length === 0
      ? XLSX.utils.aoa_to_sheet([['Nothing to show for this period.']])
      : isArrays
        ? XLSX.utils.aoa_to_sheet(s.rows as Cell[][])
        : XLSX.utils.json_to_sheet(s.rows as Record<string, Cell>[]);
    // Column widths from the longest value, so the file opens readable.
    const grid: Cell[][] = s.rows.length === 0 ? [] : isArrays
      ? (s.rows as Cell[][])
      : [Object.keys(s.rows[0] as object), ...(s.rows as Record<string, Cell>[]).map((r) => Object.values(r))];
    const widths: number[] = [];
    for (const row of grid) {
      row.forEach((c, i) => { widths[i] = Math.min(60, Math.max(widths[i] ?? 8, String(c ?? '').length + 2)); });
    }
    ws['!cols'] = widths.map((wch) => ({ wch }));
    XLSX.utils.book_append_sheet(wb, ws, sheetName(s.name, used));
  }
  XLSX.writeFile(wb, `${file}.xlsx`);
}

/** The width the page is narrowed to while printing, in CSS pixels: the
 *  printable width of A4 at the print margins (report.css). */
const PAPER_PX = { portrait: 680, landscape: 1040 };

/** The print in progress, if any: one at a time, so a second click cannot
 *  take the first one's file name for the page's own title. */
let active: (() => void) | null = null;

/** Print the current screen as a PDF-ready document. With a target (a
 *  drawer), only that element is printed: a copy of it is put on its own
 *  and the rest of the app is hidden from the printer.
 *
 *  The page goes back to normal once printing is over. Desktop browsers
 *  hold the page while their print dialog is open, so "afterprint" is the
 *  end. Phones and tablets may still be laying the document out after it,
 *  so there the page waits for the next touch or key instead. */
export async function printScreen(file: string, landscape = false, target?: HTMLElement | null): Promise<void> {
  if (active) return;
  const root = document.documentElement;
  const title = document.title;
  let isolated: HTMLElement | null = null;
  const style = document.createElement('style');
  const interactions = ['pointerdown', 'keydown', 'wheel'] as const;
  let safety = 0;

  const done = () => {
    if (active !== done) return;
    active = null;
    root.classList.remove('printing', 'print-isolated');
    isolated?.remove();
    root.style.removeProperty('--print-width');
    style.remove();
    document.title = title;
    window.removeEventListener('afterprint', afterPrint);
    for (const ev of interactions) window.removeEventListener(ev, done, true);
    window.clearTimeout(safety);
    window.dispatchEvent(new Event('resize'));
  };
  const onNextInteraction = () => {
    for (const ev of interactions) window.addEventListener(ev, done, true);
  };
  const afterPrint = () => {
    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) done();
    else onNextInteraction();
  };
  active = done;

  try {
    if (target) {
      isolated = document.createElement('div');
      isolated.id = 'print-root';
      isolated.appendChild(target.cloneNode(true));
      document.body.appendChild(isolated);
      root.classList.add('print-isolated');
    }
    style.setAttribute('data-print-orientation', '');
    style.textContent = `@page { size: A4 ${landscape ? 'landscape' : 'portrait'}; margin: ${landscape ? '10mm' : '14mm'}; }`;
    document.head.appendChild(style);
    root.style.setProperty('--print-width', `${landscape ? PAPER_PX.landscape : PAPER_PX.portrait}px`);
    root.classList.add('printing');
    document.title = file;
    // Let charts that watch their size redraw at the paper's width.
    window.dispatchEvent(new Event('resize'));
    await new Promise((r) => setTimeout(r, 400));
    window.addEventListener('afterprint', afterPrint);
    window.print();
  } catch (err) {
    done();
    throw err;
  }
  // Some browsers never say printing is over: the next touch or key ends
  // it, and ten minutes at the most.
  window.setTimeout(() => { if (active === done) onNextInteraction(); }, 1500);
  safety = window.setTimeout(done, 10 * 60 * 1000);
}
